<template>
  <button
    @click="emit('click')"
    class="w-full flex items-start gap-3 px-4 py-2.5 text-left transition-colors"
    :class="active ? 'bg-gray-50 dark:bg-gray-800/70' : 'hover:bg-gray-50 dark:hover:bg-gray-800/70'"
  >
    <!-- Provider icon -->
    <svg v-if="result.calendar_type === 'google'" class="w-5 h-5 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
      <rect width="22" height="22" x="13" y="13" fill="#fff"/>
      <polygon fill="#1e88e5" points="25.68,20.92 26.688,22.36 28.272,21.208 28.272,29.56 30,29.56 30,18.616 28.56,18.616"/>
      <path fill="#1e88e5" d="M22.943,23.745c0.625-0.574,1.013-1.37,1.013-2.249c0-1.747-1.533-3.168-3.417-3.168c-1.602,0-2.972,1.009-3.33,2.453l1.657,0.421c0.165-0.664,0.868-1.146,1.673-1.146c0.942,0,1.709,0.646,1.709,1.44c0,0.794-0.767,1.44-1.709,1.44h-0.997v1.728h0.997c1.081,0,1.993,0.751,1.993,1.64c0,0.904-0.866,1.64-1.931,1.64c-0.962,0-1.784-0.61-1.914-1.418L17,26.802c0.262,1.636,1.81,2.87,3.6,2.87c2.007,0,3.64-1.511,3.64-3.368C24.24,25.281,23.736,24.363,22.943,23.745z"/>
      <polygon fill="#fbc02d" points="34,42 14,42 13,38 14,34 34,34 35,38"/>
      <polygon fill="#4caf50" points="38,35 42,34 42,14 38,13 34,14 34,34"/>
      <path fill="#1e88e5" d="M34,14l1-4l-1-4H9C7.343,6,6,7.343,6,9v25l4,1l4-1V14H34z"/>
      <polygon fill="#e53935" points="34,34 34,42 42,34"/>
      <path fill="#1565c0" d="M39,6h-5v8h8V9C42,7.343,40.657,6,39,6z"/>
      <path fill="#1565c0" d="M9,42h5v-8H6v5C6,40.657,7.343,42,9,42z"/>
    </svg>
    <svg
      v-else
      class="w-5 h-5 flex-shrink-0 mt-0.5"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 278050 333334"
      shape-rendering="geometricPrecision"
      fill-rule="evenodd"
      clip-rule="evenodd"
    >
      <path fill="#ea3e23" d="M278050 305556l-29-16V28627L178807 0 448 66971l-448 87 22 200227 60865-23821V80555l117920-28193-17 239519L122 267285l178668 65976v73l99231-27462v-316z"/>
    </svg>

    <span class="min-w-0 flex-1">
      <span
        class="block text-sm font-medium truncate"
        :class="isPast ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'"
      >
        {{ result.title }}
      </span>
      <span
        v-if="result.calendar_identity"
        class="block text-xs truncate"
        :class="isPast ? 'text-gray-300 dark:text-gray-600' : 'text-gray-400 dark:text-gray-500'"
      >
        &lt;{{ result.calendar_identity }}&gt;
      </span>
    </span>

    <span class="flex-shrink-0 text-right">
      <span
        class="block text-sm font-semibold"
        :class="isPast ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-200'"
      >
        {{ dateLabel }}
      </span>
      <span
        class="block text-xs"
        :class="isPast ? 'text-gray-300 dark:text-gray-600' : 'text-gray-400 dark:text-gray-500'"
      >
        {{ timeLabel }}
      </span>
    </span>
  </button>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  result: { type: Object, required: true },
  active: { type: Boolean, default: false },
})

const emit = defineEmits(['click'])

const isPast = computed(() => new Date(props.result.end || props.result.start) < new Date())

const dateLabel = computed(() => {
  const date = new Date(props.result.start)
  const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const diffDays = Math.round((startOfDay(date) - startOfDay(new Date())) / 86400000)

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Tomorrow'
  if (diffDays === -1) return 'Yesterday'

  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' })
  const month = date.toLocaleDateString('en-US', { month: 'short' })
  return `${weekday} ${month} ${date.getDate()}`
})

const timeLabel = computed(() => {
  if (props.result.all_day) return 'All day'
  const start = new Date(props.result.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  const end = new Date(props.result.end).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${start}–${end}`
})
</script>
